import React, { useState } from 'react';
import { Leaf, HeartPulse, ChevronRight, Info } from 'lucide-react';
import { supabase, updateUserProfile } from '../../services/backendService';
import logger from "@/utils/logger";

interface IntentSelectionScreenProps {
  onNext: (intent: 'wellness' | 'condition', concern: string) => void;
  userId: string;
}

const IntentSelectionScreen: React.FC<IntentSelectionScreenProps> = ({ onNext, userId }) => {
  const [selection, setSelection] = useState<'wellness' | 'condition' | null>(null);
  const [concern, setConcern] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selection) return;
    
    let activeUserId = userId;
    if (!activeUserId) {
      // Fallback: try to get it directly if prop is missing
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        activeUserId = user.id;
      } else {
        logger.error('[IntentSelection] No user ID provided and none found in session.');
        return;
      }
    }
    setIsSubmitting(true);

    try {
      // 1. Update onboarding_profiles
      const { error: onboardingError } = await supabase
        .from('onboarding_profiles')
        .upsert({
          user_id: activeUserId,
          user_intent: selection,
          primary_concern: selection === 'condition' ? concern : null
        }, { onConflict: 'user_id' });

      if (onboardingError) throw onboardingError;

      // 2. Update user_profiles via backendService helper
      await updateUserProfile(activeUserId, {
        user_intent: selection,
        primary_concern: (selection === 'condition' ? concern : undefined)
      });

      logger.log('[IntentSelection] Successfully saved intent and concern');
      onNext(selection, concern);
    } catch (err) {
      logger.error('[IntentSelection] Error saving data:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-serif text-sage-900 mb-4">How can Nani help you today?</h2>
        <p className="text-lg text-sage-600 font-light">Select the path that best fits your goals.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 mb-12">
        {/* Wellness Card */}
        <button
          onClick={() => setSelection('wellness')}
          className={`relative p-8 rounded-3xl border-2 transition-all text-left group ${
            selection === 'wellness' 
              ? 'border-emerald-500 bg-emerald-50/50 shadow-lg scale-[1.02]' 
              : 'border-sage-100 bg-white hover:border-sage-300 hover:shadow-md'
          }`}
        >
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-colors ${
            selection === 'wellness' ? 'bg-emerald-500 text-white' : 'bg-emerald-50 text-emerald-600'
          }`}>
            <Leaf size={28} />
          </div>
          <h3 className="text-xl font-bold text-sage-900 mb-3">I want to understand and maintain my health</h3>
          <p className="text-sage-600 text-sm leading-relaxed mb-6">
            Learn your constitutional nature, build lasting habits, and stay ahead of imbalance.
          </p>
          <div className="flex flex-wrap gap-2">
            <span className="text-[10px] px-2 py-1 bg-white border border-sage-100 rounded-full text-sage-500">Over 40 and proactive</span>
            <span className="text-[10px] px-2 py-1 bg-white border border-sage-100 rounded-full text-sage-500">Curious about Ayurveda</span>
            <span className="text-[10px] px-2 py-1 bg-white border border-sage-100 rounded-full text-sage-500">Want to eat for my body type</span>
          </div>
          {selection === 'wellness' && (
            <div className="absolute top-4 right-4 text-emerald-500">
              <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                <ChevronRight size={16} className="text-white" />
              </div>
            </div>
          )}
        </button>

        {/* Condition Card */}
        <button
          onClick={() => setSelection('condition')}
          className={`relative p-8 rounded-3xl border-2 transition-all text-left ${
            selection === 'condition' 
              ? 'border-amber-500 bg-amber-50/50 shadow-lg scale-[1.02]' 
              : 'border-sage-100 bg-white hover:border-sage-300 hover:shadow-md'
          }`}
        >
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-colors ${
            selection === 'condition' ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-600'
          }`}>
            <HeartPulse size={28} />
          </div>
          <h3 className="text-xl font-bold text-sage-900 mb-3">I'm dealing with a specific condition or concern</h3>
          <p className="text-sage-600 text-sm leading-relaxed mb-6">
            Get targeted natural guidance for a current health challenge, to complement your existing care.
          </p>
          <div className="flex flex-wrap gap-2">
            <span className="text-[10px] px-2 py-1 bg-white border border-sage-100 rounded-full text-sage-500">High blood pressure</span>
            <span className="text-[10px] px-2 py-1 bg-white border border-sage-100 rounded-full text-sage-500">Digestive issues</span>
            <span className="text-[10px] px-2 py-1 bg-white border border-sage-100 rounded-full text-sage-500">Joint pain</span>
          </div>
          {selection === 'condition' && (
            <div className="absolute top-4 right-4 text-amber-500">
              <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center">
                <ChevronRight size={16} className="text-white" />
              </div>
            </div>
          )}
        </button>
      </div>

      {selection === 'condition' && (
        <div className="max-w-lg mx-auto mb-12 animate-in fade-in slide-in-from-top-2 duration-500">
          <label className="block text-sm font-bold text-sage-800 mb-2 px-1">What is your primary concern?</label>
          <input
            type="text"
            value={concern}
            onChange={(e) => setConcern(e.target.value)}
            placeholder="e.g. High blood pressure, Kidney disease, Fatigue"
            className="w-full p-4 rounded-2xl border-2 border-sage-100 focus:border-amber-400 focus:ring-0 transition-all outline-none"
          />
          <p className="mt-2 text-xs text-sage-500 px-1 flex items-center gap-1.5 italic">
            <Info size={12} /> This helps Nani tailor your recommendations specifically to your situation.
          </p>
        </div>
      )}

      <div className="text-center">
        <button
          onClick={handleSubmit}
          disabled={!selection || isSubmitting}
          className={`px-12 py-4 rounded-full text-lg font-bold transition-all shadow-lg ${
            !selection || isSubmitting
              ? 'bg-sage-200 text-sage-400 cursor-not-allowed shadow-none'
              : 'bg-sage-800 text-white hover:bg-sage-900 hover:scale-105 active:scale-95'
          }`}
        >
          {isSubmitting ? 'Preparing journey...' : 'Begin my journey →'}
        </button>
        <p className="mt-6 text-[11px] text-gray-400 font-medium">
          You can change this anytime from your profile.
        </p>
      </div>
    </div>
  );
};

export default IntentSelectionScreen;
